import { useCallback, useMemo, useState } from "react";
import Loader from "react-loader-spinner";
import { toastr } from "react-redux-toastr";
import { client, getApiErrorMessage } from "utils/api";

import { Dashboard } from "components";
import { Button } from "expano-components";
import useGet from "hooks/useGet";
import AddProduct from "./components/AddProduct";
import AddProductFromCategory from "./components/AddProductFromCategory";

const CampaignProducts = ({
  channel_campaign_group,
  channel_id,
  channel_campaign_id,
  refetch,
}) => {
  const [is_adding, setAdding] = useState(false);
  const [is_deleting, setDeleting] = useState(false);

  const product_ids = useMemo(() => {
    if (channel_campaign_group?.products?.length > 0) {
      return channel_campaign_group.products.map(({ id }) => id);
    }
    return [];
  }, [channel_campaign_group?.products]);

  const [is_open_add_product, setOpenAddProduct] = useState(false);
  const [is_open_add_product_from_category, setOpenAddProductFromCategory] =
    useState(false);

  const { data, is_loading } = useGet({
    query: ["campaign_channel_products", product_ids],
    path: `/channels/${channel_id}/channel_campaigns/${channel_campaign_id}/channel_campaign_groups/${channel_campaign_group.id}/products`,
    context: "ui_settings",
    search: {
      product_ids: product_ids.join(","),
    },
    error_message_enabled: false,
    options: {
      enabled: product_ids.length > 0,
    },
  });

  const handleAdd = useCallback(
    async (ids) => {
      try {
        const adding_ids = ids.filter((id) => !product_ids.includes(id));
        if(!adding_ids.length){
          throw new Error('Brak produktów do dodania')
        }
        setAdding(true);
        await client.post(
          `/channels/${channel_id}/channel_campaigns/${channel_campaign_id}/channel_campaign_groups/${channel_campaign_group.id}/channel_campaign_group_products`,
          { product_ids: adding_ids },
        );

        await refetch();
      } catch (error) {
          toastr.error("Błąd", getApiErrorMessage(error));
      } finally {
        setAdding(false);
      }
    },
    [product_ids],
  );

  const handleDelete = async (id) => {
    try {
      setDeleting(true);
      await client.delete(
        `/channels/${channel_id}/channel_campaigns/${channel_campaign_id}/channel_campaign_groups/${channel_campaign_group.id}/channel_campaign_group_products/${id}`,
      );

      await refetch();
    } catch (error) {
    } finally {
      setDeleting(false);
    }
  };

  const totals = useMemo(() => {
    if (data?.length > 0) {
      const products_count = data?.length || 0;
      const product_aliases_count = data.reduce(
        (acu, acc) => acu + acc.product_aliases_count,
        0,
      );
      const offers_count = data.reduce((acu, acc) => acu + acc.offers_count, 0);

      return {
        products_count,
        offers_count,
        product_aliases_count,
      };
    }
    return {
      products_count: 0,
      offers_count: 0,
      product_aliases_count: 0,
    };
  }, [data]);

  return (
    <Dashboard
      header={{
        icon: "channel",
        title: "Produkty",
        aside: is_loading ? (
          <Loader
            type="Watch"
            height={30}
            width={30}
            color="#36bcff"
            visible={true}
            secondaryColor="#36bcff"
            strokeWidth={2}
            strokeWidthSecondary={2}
          />
        ) : (
          <div>
            <p>
              Ilość produktów:{" "}
              <strong>
                {totals?.products_count}
                {totals?.product_aliases_count > 0
                  ? ` (w tym ${totals.product_aliases_count} aliasów)`
                  : ""}
              </strong>
            </p>
            <p className={totals?.offers_count > 1000 ? "text-danger" : ""}>
              Ilość ofert: <strong>{totals?.offers_count}/1000</strong>
            </p>
          </div>
        ),
      }}
      footer={
        <>
          <Button
            disabled={is_adding}
            type="add"
            text="Dodaj produkty"
            onClick={() => setOpenAddProduct(true)}
          />
          <Button
            disabled={is_adding}
            type="add"
            text="Dodaj produkty z kategorii"
            onClick={() => setOpenAddProductFromCategory(true)}
          />
        </>
      }
    >
      {is_open_add_product && (
        <AddProduct
          handleClose={() => setOpenAddProduct(false)}
          handleConfirm={(ids) => {
            handleAdd(ids);
            setOpenAddProduct(false);
          }}
        />
      )}
      {is_open_add_product_from_category && (
        <AddProductFromCategory
          handleClose={() => setOpenAddProductFromCategory(false)}
          handleConfirm={(ids) => {
            handleAdd(ids);
            setOpenAddProductFromCategory(false);
          }}
        />
      )}
      {data?.length > 0 && (
        <div style={{ maxHeight: "530px", overflow: "auto" }}>
          <table className="table">
            <thead
              className="table__head"
              style={{ position: "sticky", top: 0, backgroundColor: "#fff" }}
            >
              <tr>
                <th width="160px">SKU</th>
                <th width="130px">Producent</th>
                <th>Nazwa</th>
                <th width="100px">Ilość aliasów</th>
                <th width="100px">Ilość ofert</th>
                <th />
              </tr>
            </thead>
            <tbody className="table__body table-body ">
              {data.map(
                ({
                  channel_campaign_group_product_id,
                  name,
                  sku,
                  product_aliases_count,
                  offers_count,
                  producer_name,
                }) => (
                  <tr key={channel_campaign_group_product_id}>
                    <td>{sku}</td>
                    <td>{producer_name}</td>
                    <td>{name}</td>
                    <td>{product_aliases_count}</td>
                    <td>{offers_count}</td>
                    <td>
                      <Button
                        disabled={is_deleting}
                        type="delete-bin"
                        onClick={() =>
                          handleDelete(channel_campaign_group_product_id)
                        }
                      />
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
    </Dashboard>
  );
};

export default CampaignProducts;
